import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FiDownload,
  FiExternalLink,
  FiMail,
  FiPhone,
  FiCheckCircle,
  FiXCircle,
} from "react-icons/fi";
import { Link } from "react-router-dom";
import ScrollIndicator from "../Home/ScrollIndicator";
import AuthIcons from "./../Home/AuthIcons";
import { useUser } from "./../Home/UserContext";
import useScrollToTop from "./../../hooks/useScollToTop";

import HeaderBar from "./HeaderBar";
import TOC from "./TOC";
import QuickSearch from "./QuickSearch";
import VideoEmbed from "./VideoEmbed";
import SectionBlock from "./SectionBlock";
import AccordionItem from "./AccordionItem";
import Lightbox from "./Lightbox";
import FloatingAnchorBar from "./FloatingAnchorBar";
import SearchNavigator from "./SearchNavigator";

import { brandGradient, classNames } from "./utils";
import { sections, steps, faqs } from "./steps.data";

export default function Tutoriel() {
  useScrollToTop();
  const { user } = useUser();
  const [query, setQuery] = useState("");
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [currentSection, setCurrentSection] = useState("intro");

  // Recherche
  const [results, setResults] = useState([]);
  const [currentResIndex, setCurrentResIndex] = useState(0);

  // Injecte onImageClick dans les steps pour ouvrir la lightbox

  // Scroll helpers
  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const scrollToSection = (id) => {
    const sectionEl = document.getElementById(id);
    if (!sectionEl) return;
    sectionEl.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const stepsWithHandlers = useMemo(() => {
    const inject = (arr) =>
      arr.map((s) => ({
        ...s,
        onImageClick: () => setLightboxSrc(s.img),
      }));
    return Object.fromEntries(
      Object.entries(steps).map(([k, v]) => [k, inject(v)])
    );
  }, []);

  // Filtre simple
  const match = (t) =>
    !query || String(t).toLowerCase().includes(query.toLowerCase());
  const filteredFaqs = faqs.filter((f) => match(f.q) || match(f.a));

  // Mise à jour du hash d’URL en fonction de la section visible (sans "history" global)
  const contentRef = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) {
          const id = visible.target.getAttribute("id");
          const url = `${window.location.pathname}${window.location.search}#${id}`;
          window.history.replaceState(null, "", url);
          setCurrentSection(id); // 👈 suivez la section active
        }
      },
      { rootMargin: "-40% 0px -55% 0px", threshold: [0, 0.25, 0.5, 1] }
    );
    const secs = contentRef.current?.querySelectorAll("section[id]") || [];
    secs.forEach((s) => obs.observe(s));
    return () => obs.disconnect();
  }, []);

  // Reconstruit la liste des résultats à chaque changement de "query"
  useEffect(() => {
    if (!query || query.trim().length < 2) {
      setResults([]);
      setCurrentResIndex(0);
      return;
    }
    const q = query.toLowerCase();

    // 1) Steps (toutes sections)
    const stepHits = Object.entries(steps).flatMap(([sectionId, arr]) =>
      arr
        .map((s, i) => ({
          match:
            (s.title && s.title.toLowerCase().includes(q)) ||
            (s.text && s.text.toLowerCase().includes(q)),
          elementId: `step-${sectionId}-${i}`,
          sectionId,
          kind: "step",
          text: `${s.title} ${s.text}`.toLowerCase(),
        }))
        .filter((x) => x.match)
    );

    // 2) FAQs (indexés par position)
    const faqHits = faqs
      .map((f, i) => ({
        match:
          (f.q && f.q.toLowerCase().includes(q)) ||
          (f.a && f.a.toLowerCase().includes(q)),
        elementId: `faq-item-${i}`,
        sectionId: "faq",
        kind: "faq",
        text: `${f.q} ${f.a}`.toLowerCase(),
      }))
      .filter((x) => x.match);

    const all = [...stepHits, ...faqHits];

    setResults(all);
    setCurrentResIndex(0);

    // Auto-scroll vers le premier match
    if (all.length) {
      // légère latence pour laisser le DOM se mettre à jour
      setTimeout(() => {
        scrollToId(all[0].elementId);
      }, 50);
    }
  }, [query]);

  const goToResult = (index) => {
    if (!results.length) return;
    const i = (index + results.length) % results.length; // wrap
    setCurrentResIndex(i);
    scrollToId(results[i].elementId);
  };

  // QuickSearch: onEnter => résultat suivant
  const handleEnterSearch = () => {
    if (!results.length) return;
    goToResult(currentResIndex + 1);
  };

  return (
    <div
      className="pt-[130px] min-h-screen bg-slate-50 dark:bg-slate-950"
      aria-hidden
    >
      {!user && <AuthIcons />}
      <ScrollIndicator />
      <HeaderBar />

      <main className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 py-6 grid grid-cols-1 xl:grid-cols-[1fr_300px] gap-6">
        {/* Colonne principale */}
        <div ref={contentRef}>
          {/* Barre outils */}
          <div className="flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-4 mb-4">
            <QuickSearch onChange={setQuery} onEnter={handleEnterSearch} />

            <div className="flex items-center gap-2 md:ml-auto">
              {/* <a
                href="/assets/tutorial/guide-utilisateur.pdf"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-xl px-3 py-2 border border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                <FiDownload /> Guide PDF
              </a> */}
              {/* Si tu veux renvoyer vers une ancre “contact” sur l’accueil : to="/#contact" */}
              <Link
                to="/#contact"
                className="inline-flex items-center gap-2 rounded-xl px-3 py-2 border border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                <FiExternalLink /> Retour à l’accueil
              </Link>
            </div>
          </div>

          {/* Intro */}
          <section id="intro" className="scroll-mt-24">
            <a data-anchor href="#intro" className="sr-only">
              Intro
            </a>
            <div className="rounded-2xl p-6 border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
              <div className="flex items-start gap-4">
                <div
                  className={classNames(brandGradient, "w-1 rounded-full h-40")}
                />
                <div>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                    Bienvenue sur MonAppliClub
                  </h2>
                  <p className="text-slate-600 dark:text-slate-300 mt-2">
                    Ce guide vous accompagne pour{" "}
                    <span className="font-semibold">créer votre compte</span>,{" "}
                    <span className="font-semibold">
                      faire une inscription d'un adhérent
                    </span>
                    ,{" "}
                    <span className="font-semibold">
                      gérer votre dossier d'inscription
                    </span>
                    ,{" "}
                    <span className="font-semibold">
                      régler vos paiements en ligne
                    </span>
                    ,{" "}
                    <span className="font-semibold">
                      inscrire un adhérent sur une compétition
                    </span>
                    , et bien plus encore. Cliquez sur les images pour les
                    agrandir.
                  </p>
                </div>
              </div>
              {/* <div id="demo-video">
                <VideoEmbed id="AEXkqPIlsS4" />
              </div> */}
            </div>
          </section>

          {/* Sections dynamiques */}
          <SectionBlock
            id="connexion"
            title="Créer son compte utilisateur"
            items={stepsWithHandlers.connexion.filter(
              (s) => match(s.title) || match(s.text)
            )}
            query={query}
          />
          <SectionBlock
            id="megamenu"
            title="Découvrir le Menu Application"
            items={stepsWithHandlers.megamenu.filter(
              (s) => match(s.title) || match(s.text)
            )}
            query={query}
          />
          <SectionBlock
            id="utilisateurs"
            title="Les Pages Utilisateurs"
            items={stepsWithHandlers.utilisateurs.filter(
              (s) => match(s.title) || match(s.text)
            )}
            query={query}
          />
          <SectionBlock
            id="enseignants"
            title="Les Pages Enseignants"
            items={stepsWithHandlers.enseignants.filter(
              (s) => match(s.title) || match(s.text)
            )}
            query={query}
          />
          <SectionBlock
            id="administrateurs"
            title="Les Pages Administrateurs"
            items={stepsWithHandlers.administrateurs.filter(
              (s) => match(s.title) || match(s.text)
            )}
            query={query}
          />

          {/* FAQ */}
          {/* <section id="faq" className="scroll-mt-24">
            <a data-anchor href="#faq" className="sr-only">
              FAQ
            </a>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
              Questions fréquentes
            </h2>
            <div className="space-y-2">
              {filteredFaqs.length ? (
                filteredFaqs.map((f, idx) => {
                  // retrouve l'index dans le tableau complet pour un ID stable
                  const originalIndex = faqs.findIndex(
                    (x) => x.q === f.q && x.a === f.a
                  );
                  return (
                    <AccordionItem
                      key={`${f.q}-${originalIndex}`}
                      q={f.q}
                      a={f.a}
                      query={query}
                      htmlId={`faq-item-${originalIndex}`} // 👈 ID scrolable
                    />
                  );
                })
              ) : (
                <p className="text-slate-500">
                  Aucun résultat pour « {query} ».
                </p>
              )}
            </div>
          </section> */}

          {/* Satisfaction */}
          <section className="mt-8">
            <div className="rounded-2xl p-4 md:p-6 border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex items-center justify-between gap-4">
              <div>
                <p className="font-medium text-slate-900 dark:text-white">
                  Ce tutoriel vous a-t-il aidé ?
                </p>
                <p className="text-sm text-slate-500">
                  Vos retours nous aident à améliorer l’application.
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  className="inline-flex items-center gap-2 rounded-xl px-3 py-2 border border-emerald-300 text-emerald-700 dark:text-emerald-300 dark:border-emerald-800 hover:bg-emerald-50/60"
                  onClick={() => alert("Merci pour votre retour positif !")}
                >
                  <FiCheckCircle /> Oui
                </button>
                <button
                  className="inline-flex items-center gap-2 rounded-xl px-3 py-2 border border-rose-300 text-rose-700 dark:text-rose-300 dark:border-rose-800 hover:bg-rose-50/60"
                  onClick={() =>
                    alert("Dites-nous ce qui manque : support@votreclub.fr")
                  }
                >
                  <FiXCircle /> Pas vraiment
                </button>
              </div>
            </div>
          </section>

          {/* Support */}
          <section id="support" className="scroll-mt-24 mt-8">
            <a data-anchor href="#support" className="sr-only">
              Support
            </a>
            <div className="rounded-2xl p-6 border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                Besoin d’aide ?
              </h2>
              <p className="text-slate-600 dark:text-slate-300 mt-2">
                Contactez le développeur Web : Laurent VICTORIN.
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                <a
                  href="mailto:monappliclub@gmail.com"
                  className="inline-flex items-center gap-2 rounded-xl px-4 py-2 border border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
                >
                  <FiMail /> monappliclub@gmail.com
                </a>
                <a
                  href="tel:+33680727707"
                  className="inline-flex items-center gap-2 rounded-xl px-4 py-2 border border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
                >
                  <FiPhone /> 06 80 72 77 07
                </a>
              </div>
            </div>
          </section>
        </div>

        {/* Colonne TOC */}
        <TOC items={sections} />
      </main>

      {/* Lightbox */}
      <Lightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />

      {/* Barre de navigation des résultats de recherche */}
      <SearchNavigator
        query={query}
        total={results.length}
        currentIndex={currentResIndex}
        onPrev={() => goToResult(currentResIndex - 1)}
        onNext={() => goToResult(currentResIndex + 1)}
        onClear={() => {
          setQuery("");
          setResults([]);
          setCurrentResIndex(0);
        }}
      />

      {/* Mini barre d’ancrage flottante (mobile/desktop) */}
      <FloatingAnchorBar
        sections={sections}
        activeId={currentSection}
        onClick={scrollToSection}
      />
    </div>
  );
}
